import { useEffect, useState } from 'react';
import { goalsAPI } from '../lib/api/goals';
import { Goal, Milestone, DailyTask, GoalStats, GoalDuration } from '../types/goals';
import { PaginationData } from '../pages/GoalsPage';

export interface UseGoalsState {
  goals: Goal[];
  milestones: Milestone[];
  dailyTasks: DailyTask[];
  stats: GoalStats | null;
  pagination: PaginationData | null;
  isLoading: boolean;
  error: string | null;
  generalStats: GoalStats | null;
}

export interface UseGoalsFilters {
  page?: number;
  limit?: number;
  completed?: boolean;
  duration?: GoalDuration;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startDate?: Date;
  endDate?: Date;
  searchTerm?: string;
}


export function useGoals(initialFilters?: UseGoalsFilters) {
  const [state, setState] = useState<UseGoalsState>({
    goals: [],
    milestones: [],
    dailyTasks: [],
    stats: null,
    pagination: null,
    isLoading: false,
    error: null,
    generalStats: null
  });

  const defaultFilters: UseGoalsFilters = {
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  };

  const [filters, setFilters] = useState<UseGoalsFilters>({
    ...defaultFilters,
    ...initialFilters
  });

  const fetchGoals = async (newFilters?: UseGoalsFilters) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const response = await goalsAPI.getGoals(newFilters || filters);

      setState(prev => ({
        ...prev,
        goals: response.data || [],
        pagination: response.pagination || null,
        isLoading: false
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        goals: [],
        stats: null,
        pagination: null,
        error: err instanceof Error ? err.message : 'Failed to fetch goals',
        isLoading: false
      }));
      throw err;
    }
  };

  const fetchGoalDetails = async (goalId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const { milestones, dailyTasks, stats } = await goalsAPI.getGoalDetails(goalId);

      setState(prev => ({
        ...prev,
        milestones,
        dailyTasks,
        stats,
        isLoading: false
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to fetch goal details',
        isLoading: false
      }));
      throw err;
    }
  };

  const fetchGoalsStats = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const stats = await goalsAPI.getGoalsStats();

      setState(prev => ({
        ...prev,
        generalStats: stats,
        isLoading: false
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to fetch goals stats',
        isLoading: false
      }));
      throw err;
    }
  }

  const createGoal = async (data: {
    title: string;
    description: string;
    startDate: Date;
    endDate: Date;
    duration: GoalDuration;
  }) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await goalsAPI.createGoal(data);

      setState(prev => ({
        ...prev,
        goals: [response.goal, ...prev.goals],
        stats: response.stats, // Actualizar estadísticas después de crear
        isLoading: false
      }));

      return response;
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to create goal',
        isLoading: false
      }));
      throw err;
    }
  };

  const updateGoalProgress = async (goalId: string, progress: number) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const updatedGoal = await goalsAPI.updateGoalProgress(goalId, progress);

      setState(prev => ({
        ...prev,
        goals: prev.goals.map(goal =>
          goal._id === goalId ? updatedGoal : goal
        ),
        isLoading: false
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to update goal progress',
        isLoading: false
      }));
      throw err;
    }
  };

  const updateFilters = (newFilters: Partial<UseGoalsFilters>) => {
    const updatedFilters = { ...filters, ...newFilters, page: 1 };
    setFilters(updatedFilters);
    fetchGoals(updatedFilters);
  };

  useEffect(() => {
    fetchGoals(filters);
    fetchGoalsStats();
  }, []);

  return {
    ...state,
    filters,
    fetchGoals,
    fetchGoalDetails,
    createGoal,
    updateGoalProgress,
    updateFilters,
    fetchGoalsStats,
    setState,
  };
}